/**
 * Quotation visibility by role — the FRONTEND MIRROR of the server contract.
 *
 * The security boundary is the backend and the database (see
 * ``backend/quowada/quotation_access.py`` and the RLS policies of
 * ``20260814120000_offertrace_quotation_team_attribution.sql``). This module only
 * decides whether a cross-user control makes sense on screen: a member who can
 * never receive somebody else's quotation must not be offered a filter that
 * promises one.
 *
 * owner   → every quotation of the organization;
 * manager → its own quotations PLUS the quotations of the active team it
 *           currently manages (Phase 2D.3B.1: no longer organization-wide);
 * else    → only the quotations they currently own (fail closed for an unknown
 *           or missing role label).
 */

export const ORG_WIDE_QUOTATION_ROLES = ["owner"];
export const TEAM_SCOPED_QUOTATION_ROLES = ["manager"];

export function seesEveryOrgQuotation(role) {
  return ORG_WIDE_QUOTATION_ROLES.includes(role);
}

export function seesTeamQuotations(role) {
  return TEAM_SCOPED_QUOTATION_ROLES.includes(role);
}

export function ownQuotationsOnly(role) {
  return !seesEveryOrgQuotation(role) && !seesTeamQuotations(role);
}

/**
 * The OPEN statuses — the mirror of ``private.member_open_quotation_statuses()``
 * (migration 21). Terminal quotations keep their owner and their team forever, so
 * no reassignment control may be offered for them.
 */
export const OPEN_QUOTATION_STATUSES = ["draft", "sent", "opened", "question"];

export function isOpenQuotation(status) {
  return OPEN_QUOTATION_STATUSES.includes(status);
}

/**
 * Phase 2D.3B.2 — may this Manager be OFFERED the reassignment control?
 *
 * The decision is the database's (public.manager_reassign_quotation_owner); this
 * only avoids showing an action that would be refused. ``candidateUserIds`` is
 * the server-scoped list from GET /organization/members/reassignable: the active
 * Sales of the team this Manager currently leads, WITHOUT the Manager itself. So
 * an Owner-owned quotation, the Manager's own quotation, another team's
 * quotation, a team-free Sales quotation and an inactive Sales quotation all fail
 * the membership test below without any wider roster ever reaching the browser.
 */
export function managerMayReassign({ role, status, ownerUserId, candidateUserIds }) {
  if (!seesTeamQuotations(role)) return false;
  if (!isOpenQuotation(status)) return false;
  if (!ownerUserId) return false;
  const ids = candidateUserIds || [];
  if (!ids.includes(ownerUserId)) return false;
  return ids.some((id) => id !== ownerUserId);
}
